import React, { useCallback } from 'react';
import styles from './styles/ScenarioTitlePageTags.module.css';
import { addTagToScenario, removeTagFromScenario } from '../../../services/TagServices';
import TagSearch from './TagSearch';

const MAX_TAGS = 6;

const ScenarioTitlePageTags = ({ tags, setTags, scenarioId, userId, locked = false }) => {
    const atLimit = tags.length >= MAX_TAGS;

    const applyTag = useCallback(
        async (nameOrObj) => {
            const name = typeof nameOrObj === 'string' ? nameOrObj : nameOrObj.name;
            const trimmed = name.trim().toLowerCase();
            if (!trimmed) return;
            if (tags.some((t) => t.name.toLowerCase() === trimmed)) return;
            if (tags.length >= MAX_TAGS) return;

            try {
                const { tag } = await addTagToScenario(scenarioId, trimmed, userId);
                setTags((prev) => [...prev, tag]);
            } catch (err) {
                console.error('Error adding tag:', err);
            }
        },
        [tags, scenarioId, userId, setTags]
    );

    const handleRemoveTag = useCallback(
        async (tag) => {
            try {
                await removeTagFromScenario(scenarioId, tag._id, userId);
                setTags((prev) => prev.filter((t) => t._id !== tag._id));
            } catch (err) {
                console.error('Error removing tag:', err);
            }
        },
        [scenarioId, userId, setTags]
    );

    const handleClearAll = useCallback(async () => {
        await Promise.all(
            tags.map((tag) => removeTagFromScenario(scenarioId, tag._id, userId))
        );
        setTags([]);
    }, [tags, scenarioId, userId, setTags]);

    return (
        <div className={styles.tagsWrapper}>
            <h2 className={styles.sectionTitle}>Tags</h2>
            <p className={styles.helperText}>
                Add up to {MAX_TAGS} tags. Type to search existing tags or create new ones.
                Press Enter or comma to confirm.
            </p>

            <TagSearch
                onSelectTag={applyTag}
                selectedTags={tags}
                onRemoveTag={locked ? undefined : handleRemoveTag}
                allowCreate
                disabled={atLimit || locked}
                placeholder={
                    atLimit ? `Tag limit reached (${MAX_TAGS})` : 'Search or add a tag...'
                }
            />

            <div className={styles.detailsFooter}>
                <p className={styles.tagsRemainingText}>
                    <span>{Math.max(0, MAX_TAGS - tags.length)}</span> tags remaining
                </p>
                {tags.length > 0 && !locked && (
                    <button
                        type="button"
                        onClick={handleClearAll}
                        className={`${styles.button} ${styles.clearAllButton}`}
                    >
                        Clear All
                    </button>
                )}
            </div>
        </div>
    );
};

export default React.memo(ScenarioTitlePageTags);
